import { useRef, useState } from "react";
import { Plus, X, Upload, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Panel } from "@/components/common";
import { WidgetPicker } from "@/components/WidgetPicker";
import { OrgCode } from "@/components/OrgCode";
import { LocationsPanel } from "@/components/Locations";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, assetUrl } from "@/lib/api";

const SWATCHES = ["#009B77", "#0E8A5A", "#1F6FEB", "#C2410C", "#7C3AED", "#171717"];
const COUNTRIES = [["IT", "Italia"], ["CH", "Svizzera"], ["FR", "Francia"], ["DE", "Germania"], ["ES", "Spagna"], ["AT", "Austria"], ["PT", "Portogallo"], ["GB", "Regno Unito"]];
const UNITS = [["1", "minuti"], ["60", "ore"], ["1440", "giorni"]];
const split = (m) => (m % 1440 === 0 ? [m / 1440, "1440"] : m % 60 === 0 ? [m / 60, "60"] : [m, "1"]);

function ReminderEditor({ value, onChange, email, onEmail }) {
  const keys = useRef([]);
  while (keys.current.length < value.length) keys.current.push(crypto.randomUUID());
  keys.current.length = value.length;
  const rows = value.map(split);
  const update = (i, n, u) => onChange(rows.map(([a, b], j) => (j === i ? Number(n) * Number(u) : a * Number(b))));
  return (
    <Panel title="Promemoria incontri" testId="settings-reminders">
      <div className="space-y-3 p-5">
        <p className="text-sm text-muted-foreground">Quanto tempo prima di un incontro accettato inviare i promemoria (max 5, da 5 minuti a 30 giorni).</p>
        {rows.map(([n, u], i) => (
          <div key={keys.current[i]} className="flex items-center gap-2" data-testid={`reminder-row-${i}`}>
            <Input type="number" min={1} className="w-24" value={n} onChange={(e) => update(i, e.target.value, u)} data-testid={`reminder-value-${i}`} />
            <Select value={u} onValueChange={(v) => update(i, n, v)}>
              <SelectTrigger className="w-32" data-testid={`reminder-unit-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent>{UNITS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">prima</span>
            <Button type="button" size="icon" variant="ghost" onClick={() => { keys.current.splice(i, 1); onChange(value.filter((_, j) => j !== i)); }} data-testid={`reminder-remove-${i}`}><X className="h-4 w-4" /></Button>
          </div>
        ))}
        {value.length < 5 && (
          <Button type="button" size="sm" variant="outline" onClick={() => onChange([...value, 60])} data-testid="reminder-add-button"><Plus className="mr-1 h-3.5 w-3.5" />Aggiungi promemoria</Button>
        )}
        <label className="flex items-center justify-between rounded-md border px-3 py-2.5 text-sm">
          <span><span className="block">Invia i promemoria anche via email</span><span className="text-xs text-muted-foreground">Al richiedente e ai partecipanti che hanno accettato</span></span>
          <Switch checked={email} onCheckedChange={onEmail} data-testid="reminder-email-switch" />
        </label>
      </div>
    </Panel>
  );
}

export default function Personalizzazione() {
  const { settings } = useAuth();
  return settings ? <SettingsForm /> : null;
}

function SettingsForm() {
  const { settings, setSettings } = useAuth();
  const [f, setF] = useState(settings);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const logoRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("Logo troppo grande (max 2 MB)");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/logo", fd);
      setSettings(data);
      setF((x) => ({ ...x, logo_url: data.logo_url }));
      toast.success("Logo caricato");
    } catch (e2) { toast.error(errMsg(e2)); } finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put("/admin/settings", {
        ...f, default_leave_days: Number(f.default_leave_days), default_permission_hours: Number(f.default_permission_hours),
      });
      setSettings(data);
      toast.success("Personalizzazione salvata");
    } catch (e2) {
      toast.error(errMsg(e2));
    }
  };

  return (
    <div data-testid="branding-page">
      <PageHeader title="Personalizzazione" subtitle="Brand, tema e impostazioni aziendali predefinite." />
      <OrgCode compact />
      <div className="mb-6"><LocationsPanel /></div>
      <form onSubmit={save} className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Panel title="Identità">
            <div className="grid gap-4 p-5 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Nome app</Label><Input value={f.app_name} onChange={set("app_name")} required data-testid="settings-app-name-input" /></div>
              <div className="space-y-1.5"><Label>Nome azienda</Label><Input value={f.company_name} onChange={set("company_name")} data-testid="settings-company-name-input" /></div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Logo aziendale</Label>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-md border bg-white" data-testid="settings-logo-preview">
                    {f.logo_url ? <img src={assetUrl(f.logo_url)} alt="logo" className="h-full w-full object-contain p-1" /> : <ImageIcon className="h-5 w-5 text-muted-foreground" />}
                  </div>
                  <input ref={logoRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadLogo} data-testid="settings-logo-file-input" />
                  <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => logoRef.current?.click()} data-testid="settings-logo-upload-button">
                    <Upload className="mr-1.5 h-4 w-4" />{uploading ? "Caricamento…" : f.logo_url ? "Cambia logo" : "Carica logo"}
                  </Button>
                  {f.logo_url && <Button type="button" variant="ghost" size="sm" onClick={() => setF({ ...f, logo_url: "" })} data-testid="settings-logo-remove-button">Rimuovi</Button>}
                </div>
                <p className="text-xs text-muted-foreground">PNG, JPG o WEBP, max 2 MB. Consigliato quadrato con sfondo trasparente. Rimuovi e poi salva per tornare all'iniziale.</p>
              </div>
            </div>
          </Panel>
          <Panel title="Colori e tema">
            <div className="space-y-4 p-5">
              <div className="space-y-2">
                <Label>Colore primario</Label>
                <div className="flex flex-wrap items-center gap-2">
                  {SWATCHES.map((c) => (
                    <button key={c} type="button" onClick={() => setF({ ...f, primary_color: c })} style={{ background: c }} aria-label={c}
                      className={`h-8 w-8 rounded-md ring-offset-2 ring-offset-background transition-transform hover:scale-110 ${f.primary_color === c ? "ring-2 ring-foreground" : ""}`} data-testid={`settings-swatch-${c.slice(1)}`} />
                  ))}
                  <input type="color" value={f.primary_color} onChange={set("primary_color")} className="h-8 w-10 cursor-pointer rounded border bg-transparent" data-testid="settings-color-picker" />
                  <Input value={f.primary_color} onChange={set("primary_color")} className="w-28 font-mono" data-testid="settings-color-input" />
                </div>
              </div>
              <div className="max-w-xs space-y-1.5">
                <Label>Tema predefinito</Label>
                <Select value={f.default_theme} onValueChange={(v) => setF({ ...f, default_theme: v })}>
                  <SelectTrigger data-testid="settings-theme-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="light">Giorno</SelectItem><SelectItem value="dark">Notte</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          </Panel>
          <Panel title="Valori predefiniti">
            <div className="grid gap-4 p-5 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Giorni ferie annui</Label><Input type="number" min={0} value={f.default_leave_days} onChange={set("default_leave_days")} data-testid="settings-leave-days-input" /></div>
              <div className="space-y-1.5"><Label>Ore permesso annue</Label><Input type="number" min={0} value={f.default_permission_hours} onChange={set("default_permission_hours")} data-testid="settings-permission-hours-input" /></div>
              <div className="space-y-1.5">
                <Label>Paese (festività nazionali)</Label>
                <Select value={f.country || "IT"} onValueChange={(v) => setF({ ...f, country: v })}>
                  <SelectTrigger data-testid="settings-country-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{COUNTRIES.map(([c, l]) => <SelectItem key={c} value={c}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </Panel>
          <ReminderEditor value={f.meeting_reminders || []} onChange={(v) => setF({ ...f, meeting_reminders: v })}
            email={f.meeting_reminder_email !== false} onEmail={(c) => setF({ ...f, meeting_reminder_email: c })} />
          <Panel title="Dashboard dei dipendenti" testId="settings-dashboard-panel">
            <div className="space-y-3 p-5">
              <p className="text-sm text-muted-foreground">Scegli cosa vedono i dipendenti nella loro dashboard. I servizi restano sempre accessibili dal menu. Puoi personalizzarla per singolo dipendente in Dipendenti.</p>
              <WidgetPicker prefix="settings" value={f.employee_dashboard_widgets || []} onChange={(v) => setF({ ...f, employee_dashboard_widgets: v })} />
            </div>
          </Panel>
          <Button type="submit" data-testid="settings-save-button">Salva modifiche</Button>
        </div>
        <Panel title="Anteprima" className="h-fit overflow-hidden" testId="settings-preview">
          <div className="flex items-center gap-3 bg-[var(--brand-bg)] p-5 text-white">
            {f.logo_url ? <img src={assetUrl(f.logo_url)} alt="" className="h-9 w-9 rounded bg-white object-contain p-1" /> :
              <div className="font-display flex h-9 w-9 items-center justify-center rounded-md text-lg font-extrabold" style={{ background: f.primary_color }}>{(f.app_name || "N")[0]}</div>}
            <div><p className="font-display font-bold">{f.app_name}</p><p className="text-xs text-white/50">{f.company_name}</p></div>
          </div>
          <div className="space-y-3 p-5">
            <div className="rounded-md px-4 py-2 text-center text-sm font-medium text-white" style={{ background: f.primary_color }}>Pulsante principale</div>
            <div className="h-2 rounded-full bg-muted"><div className="h-full w-2/3 rounded-full" style={{ background: f.primary_color }} /></div>
          </div>
        </Panel>
      </form>
    </div>
  );
}
